// Ambient / sleep (docs/AUDIO_DESIGN.md §12): floating warm pads for sleep and deep focus. Two-bar maj9 / add9 / lydian /
// m9 / m11 chords held (tied) across bars in wide open voicings, a soft sub holding whole notes, a few glass bells in a
// long dotted delay; no beat in chill, a shaker whisper in balanced, a soft kick + shaker in upbeat. Instruments are in
// ambient-voices.js.
import { QUALITIES, bassNote, voice } from '../harmony.js';
import { createPad, createSub, createFeltKit, createBell } from './ambient-voices.js';

const ENERGIES = ['chill', 'balanced', 'upbeat'];
const mod12 = n => ((n % 12) + 12) % 12;

/* ---------------- voicing: wide open 5-note pads with a smooth top ---------------- */
const CANDS = new Map();
const gapOk = (lo, hi) => hi - lo >= (lo < 60 ? 3 : 2) && hi - lo <= 9;
/** Candidates for a chord (root pc, quality): the root or 5th at MIDI 47–58, then 4 chord tones above it, a 4th to an
 *  octave first and 2–9 semitones after (3+ below MIDI 60), the top at 67–84; the 3rd (or sus tone), the 7th / 6th and
 *  the colour tone (♯11, or the 11 on m11) all present; at most one pitch class doubled. → [{ v, mean, ninth }]. Cached. */
function padCands(root, q) {
  const id = `${root}:${q}`;
  if (CANDS.has(id)) return CANDS.get(id);
  const iv = [...new Set(QUALITIES[q].map(mod12))], has = i => iv.includes(i), pcs = iv.map(i => mod12(root + i));
  const need = [[4, 3, 2, 5].find(has), [11, 10, 9].find(has), has(6) ? 6 : has(3) && has(5) ? 5 : null]
    .filter(i => i != null).map(i => mod12(root + i));
  const ninth = has(2) ? mod12(root + 2) : null, out = [];
  for (const b0 of has(7) ? [0, 7] : [0]) {
    const b = 47 + mod12(root + b0 - 47), pool = [];
    for (let m = b + 5; m <= 84; m++) if (pcs.includes(m % 12)) pool.push(m);
    const n = pool.length;
    for (let i = 0; i < n && pool[i] - b <= 12; i++) for (let j = i + 1; j < n && pool[j] - pool[i] <= 9; j++) {
      if (!gapOk(pool[i], pool[j])) continue;
      for (let k = j + 1; k < n && pool[k] - pool[j] <= 9; k++) {
        if (!gapOk(pool[j], pool[k])) continue;
        for (let l = k + 1; l < n && pool[l] <= 84 && pool[l] - pool[k] <= 9; l++) {
          if (!gapOk(pool[k], pool[l]) || pool[l] < 67) continue;
          const v = [b, pool[i], pool[j], pool[k], pool[l]], pc = v.map(m => m % 12);
          if (new Set(pc).size < 4 || !need.every(x => pc.includes(x))) continue;
          out.push({ v, mean: v.reduce((s, m) => s + m, 0) / 5, ninth: ninth != null && pc.includes(ninth) });
        }
      }
    }
  }
  CANDS.set(id, out);
  return out;
}
/** Cheapest candidate after prev: 0.3·|mean − 64| + 1.5 per semitone of top outside 71–79, −1.5 with the 9th (the first
 *  pad: 64 and 71–79 moved by the track's register, plan.register) + Σ|Δ| over the sorted voices + 2 per semitone of top
 *  motion beyond 3. A chord whose voicing can stay where it is (the same chord held on) keeps it, so the pad ties across
 *  the bar. */
function padVoice(sp, prev, c, plan) {
  const cands = padCands(mod12(plan.key.tonic + c.root), c.q);
  if (!cands.length) return voice(sp, prev, plan.register);
  if (prev && prev.length === 5 && cands.some(({ v }) => v.every((m, i) => m === prev[i]))) return prev.slice();
  const r = prev ? 0 : plan.register;
  let best = cands[0].v, bc = Infinity;
  for (const { v, mean, ninth } of cands) {
    let k = 0.3 * Math.abs(mean - 64 - r) + 1.5 * Math.max(0, v[4] - 79 - r, 71 + r - v[4]) - (ninth ? 1.5 : 0);
    if (prev) {
      for (let i = 0; i < Math.min(5, prev.length); i++) k += Math.abs(v[i] - prev[i]);
      k += 2 * Math.max(0, Math.abs(v[4] - prev[prev.length - 1]) - 3);
    }
    if (k < bc) { bc = k; best = v; }
  }
  return best.slice();
}

/* ---------------- plan: pad timbre, drums off in chill, a sparser lead motif ---------------- */
const SPARSE = [[0, 12], [0, 8, 20], [4, 16], [0, 10, 22], [2, 14, 24], [0, 6, 20]];     // 2-bar bell cells (16th steps)
//                   chill          balanced       upbeat
const PAD = {
  atk:   [[2.2, 3.2],   [1.5, 2.4],   [0.9, 1.6]],       // seconds to ~95 % (τ = atk/3)
  rel:   [[1.2, 1.6],   [1.0, 1.35],  [0.75, 1.05]],     // release τ (audible 4τ ≈ 3–6.5 s: chords overlap and cross-fade)
  drone: [0.75,         0.5,          0.3],              // odds of a tonic drone under the pad
  move:  [0,            0.12,         0.3],              // odds of a 5th in the 2nd bar of a held bass note
};
function plan(p, { r, energy }) {
  const e = Math.max(0, ENERGIES.indexOf(energy)), rnd = (a, b, d = 3) => +(a + r() * (b - a)).toFixed(d);
  p.ep.pad = {
    atk: rnd(...PAD.atk[e], 2), rel: rnd(...PAD.rel[e], 2), det: rnd(5, 12, 1), shim: rnd(0.08, 0.16, 2), lfo: rnd(0.05, 0.16),
    depth: p.ep.tremDepth, width: rnd(0.3, 0.55, 2), wash: rnd(0.18, 0.3, 2), drone: r() < PAD.drone[e],
  };
  p.bass.move = PAD.move[e];
  if (energy === 'chill') for (const s of p.sections) s.layers.drums = 'none';
  if (p.lead) {
    const m = p.lead.motif, steps = SPARSE[Math.floor(r() * SPARSE.length)], n = steps.length;
    p.lead.motif = {
      ...m, steps, contour: m.contour.slice(0, n), vel: steps.map((s, i) => (i ? rnd(0.58, 0.68, 2) : 0.72)),
      durs: steps.map((s, i) => (i < n - 1 ? Math.min(8, steps[i + 1] - s) : 8 + Math.floor(r() * 5))),
    };
  }
}

/* ---------------- bass: whole notes on the chord roots (a soft sub) ---------------- */
function bass({ plan: p, st, b, mode, chords }) {
  const line = [];
  for (const c of chords) {
    const s = c.start * 4, len = c.beats * 4, m = bassNote(mod12(p.key.tonic + c.root), st.prevBass);
    if (b.sec === 'outro' && b.j === 2) line.push({ s, midi: m, vel: 0.8, dur: 30 });             // the final chord rings out
    else if (mode === 'kick' && !c.onset && len === 16 && st.rS() < p.bass.move) {                // a held chord's 2nd bar
      line.push({ s, midi: m, vel: 0.74, dur: 8 }, { s: s + 8, midi: m + 7 <= 50 ? m + 7 : m - 5, vel: 0.7, dur: 8 });
    } else line.push({ s, midi: m, vel: c.onset ? 0.8 : 0.76, dur: len });
    st.prevBass = m;
  }
  return line;
}

export default {
  id: 'ambient', name: 'Ambient', desc: 'Slow warm pads and long chords, barely a beat',
  vary: 'colour',
  energy: {
    //          chill                         balanced                                  upbeat
    bpm:        [[54, 60],                    [58, 66],                                 [64, 74]],     // 54: the 54-bar forms stay ≤ 240 s
    swing:      [[50, 52],                    [50, 52],                                 [50, 52]],
    snareLag:   [[0, 0.004],                  [0, 0.006],                               [0.002, 0.008]],
    stretch:    [0.9,                         0.75,                                     0.6],          // odds of 2-bar chords
    groove:     [[{ W1: 1 }, { W1: 1 }],      [{ W1: 1.2, W2: 1, W3: 0.7 }, { W1: 1.2, W2: 1, W3: 0.7 }], [{ U1: 1, U2: 1, U3: 0.8 }, { U1: 1, U2: 1, U3: 0.8 }]],
    drumLevel:  [0.5,                         0.5,                                      0.62],
    kitLp:      [[2400, 3200],                [2600, 3600],                             [3000, 4200]],
    epLp:       [[900, 1400],                 [1100, 1800],                             [1400, 2300]], // pad bus LP
    tremDepth:  [[0.2, 0.35],                 [0.18, 0.3],                              [0.15, 0.25]], // pad LP LFO depth (× lp)
    strum:      [[0.03, 0.06],                [0.03, 0.06],                             [0.03, 0.05]],
    epVel:      [[0.48, 0.58],                [0.54, 0.64],                             [0.58, 0.68]],
    comp:       [{ hold: [1, 1.4], bloom: [0.3, 0.6], late: [0.1, 0.3], swell: [0, 0] },
      { hold: [0.8, 1.2], bloom: [0.4, 0.8], late: [0.2, 0.4], swell: [0, 0.15] },
      { hold: [0.5, 0.9], bloom: [0.5, 0.9], late: [0.2, 0.4], swell: [0.2, 0.5] }],
    push:       [0,                           0.04,                                     0.08],
    lead:       [0.2,                         0.45,                                     0.6],
    softLead:   [0.8,                         0.6,                                      0.45],
    tone:       [[3200, 4600],                [3800, 5600],                             [4600, 6800]],
    wowCents:   [[4, 7],                      [3.5, 6.5],                               [3, 6]],
    dust:       [[0.25, 0.5],                 [0.3, 0.6],                               [0.35, 0.7]],
    introHats:  [0,                           0.1,                                      0.35],
    leadFrom:   [2,                           2,                                        1],
    hatsFirst:  [0,                           0.25,                                     0.25],
    breakDrums: [0,                           0.25,                                     0.5],
    breakLead:  [0.2,                         0.35,                                     0.5],
    // development (arrange.js BY_ENERGY): no lift (the drone), no solo; the pads open alone or under the melody
    lift:       [0, 0, 0],
    solo:       [0, 0, 0],
    intro:      [{ keys: 0.65, beat: 0, groove: 0, lead: 0.35, cold: 0 }, { keys: 0.55, beat: 0.15, groove: 0, lead: 0.3, cold: 0 }, { keys: 0.45, beat: 0.2, groove: 0.1, lead: 0.25, cold: 0 }],
  },
  progressions: {
    major: {
      A1: [[0, 'maj9', 4], [5, 'maj9s11', 4], [0, 'maj9', 4], [5, 'maj9s11', 4]],     // I – IV (lydian IV), floating
      A2: [[0, 'maj9', 4], [0, 'add9', 4], [9, 'm9', 4], [9, 'm11', 4]],              // I – vi
      A3: [[0, 'maj9', 4], [0, 'maj9s11', 4], [0, 'maj9', 4], [5, 'maj9', 4]],        // I – I lydian colour – IV
      A4: [[5, 'maj9', 4], [0, 'add9', 4], [5, 'maj9', 4], [0, '69', 4]],             // IV – I
      A5: [[0, 'maj9', 4], [4, 'm7', 4], [5, 'maj9', 4], [5, 'maj9s11', 4]],          // I – iii – IV
      A6: [[0, 'maj7sus2', 4], [0, 'maj9', 4], [9, 'madd9', 4], [5, 'maj9', 4]],      // Isus2 – I – vi – IV
    },
    minor: {
      a1: [[0, 'm9', 4], [0, 'm11', 4], [8, 'maj9', 4], [8, 'maj9s11', 4]],           // i – ♭VI
      a2: [[0, 'm9', 4], [5, 'm9', 4], [0, 'm11', 4], [5, 'm9', 4]],                  // i – iv
      a3: [[0, 'm9', 4], [5, 'add9', 4], [0, 'm11', 4], [5, '69', 4]],                // i – IV (Dorian)
      a4: [[0, 'm9', 4], [3, 'maj9', 4], [8, 'maj9', 4], [10, 'add9', 4]],            // i – ♭III – ♭VI – ♭VII
      a5: [[8, 'maj9s11', 4], [8, 'maj9', 4], [0, 'm9', 4], [0, 'm11', 4]],           // ♭VI – i
      a6: [[0, 'madd9', 4], [7, 'm7', 4], [8, 'maj9', 4], [0, 'm9', 4]],              // i – v – ♭VI – i
    },
  },
  turnarounds: { major: [[5, 'maj9', 4]], minor: [[8, 'maj9', 4]] },                   // a soft plagal bar, no ii–V
  grids: {
    W1: { name: 'breath', X: 'x.......s.......' },
    W2: { name: 'sand', X: 'x...s...x...s...' },
    W3: { name: 'tock', X: '....s.......s...', R: 's.......s.......' },
    U1: { name: 'drift', K: 'x.........x.....', X: '..s...s...s...s.' },
    U2: { name: 'glow', K: 'x.....x.........', X: 'x.s.x.s.x.s.x.s.', R: '........s.......' },
    U3: { name: 'tide', K: 'x.......x.......', X: '....x.......x...' },
    break: { X: 'x.......x.......' },
  },
  fills: {
    F1: { stop: 12 },
    F2: { roll: [[12, 0.3], [13, 0.35], [14, 0.45], [15, 0.55]], voice: 'shaker' },
    F3: { kicks: [14], voice: 'rim' },
  },
  bVariation: false,
  comps: {
    hold: [[0, 16]],                                          // held (and tied across bars)
    bloom: [[0, 16, 1, [0, 1, 2]], [6, 10, 0.85, [3, 4]]],    // the top two unfold 1.5 beats later
    late: [[4, 12, 0.9]],                                     // enters on beat 2: a breath, then a swell
    swell: [[0, 7], [8, 8, 0.85]],                            // re-articulated on beat 3
  },
  keysLevel: 1.06,                                            // the pad is most of the band: keys only needs +0.5 dB, not +3
  plan,
  voicing: padVoice,
  bass,
  voices(ac, dry, wet, p, ctx) {
    return {
      ep: createPad(ac, dry, wet, p, ctx),
      bass: createSub(ac, dry, ctx),
      kit: createFeltKit(ac, dry, wet, p.drums.kit, ctx),
      lead: p.lead ? createBell(ac, dry, wet, p.lead, ctx) : undefined,
    };
  },
};
